import imageCompression from 'browser-image-compression';

const MAX_DIMENSION = 2560; // Max width/height
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
const TARGET_SIZE = 2 * 1024 * 1024; // Target 2MB per image

export interface CompressionResult {
  compressed: File;
  originalSize: number;
  compressedSize: number;
  quality: number;
}

/**
 * Compresses an image file with progressive quality reduction
 * @param file - Original image file
 * @param maxFileSize - Maximum file size in bytes (default 5MB)
 * @returns Compressed file with metadata
 */
export const compressImage = async (
  file: File,
  maxFileSize: number = MAX_FILE_SIZE
): Promise<CompressionResult> => {
  try {
    // Determine output format - prefer JPEG for photos, keep PNG for transparency
    const isPNG = file.type === 'image/png';
    const outputFormat = isPNG ? 'image/png' : 'image/jpeg';
    
    // Progressive compression options - start with high quality (0.85)
    let quality = 0.85;
    let compressedFile: File | Blob = file;
    let attempts = 0;
    const maxAttempts = 6; // Try up to 6 quality levels (0.85, 0.75, 0.65, 0.55, 0.45, 0.35)
    let finalQuality = quality;

    while (attempts < maxAttempts) {
      const options = {
        maxSizeMB: maxFileSize / (1024 * 1024), // Convert to MB
        maxWidthOrHeight: MAX_DIMENSION,
        useWebWorker: true,
        fileType: outputFormat,
        initialQuality: quality,
        alwaysKeepResolution: false,
      };

      const result = await imageCompression(file, options);
      compressedFile = result;
      finalQuality = quality;
      
      // Ensure compressedFile is a File instance (browser-image-compression should return File, but verify)
      if (!(compressedFile instanceof File)) {
        // If it's a Blob, convert to File
        const arrayBuffer = await (compressedFile as Blob).arrayBuffer();
        compressedFile = new File(
          [arrayBuffer],
          file.name,
          {
            type: compressedFile.type || 'image/jpeg',
            lastModified: Date.now(),
          }
        );
      }
      
      // If file is under max size, we're done
      if (compressedFile.size <= maxFileSize) {
        break;
      }
      
      // Reduce quality for next attempt
      quality -= 0.1;
      attempts++;
    }

    // Final check - if still too large, reject
    if (compressedFile.size > maxFileSize) {
      throw new Error(`Image still exceeds ${Math.round(maxFileSize / 1024 / 1024)}MB limit after compression`);
    }

    // Ensure the compressed file has the same name as the original and is a proper File instance
    // browser-image-compression should return a File, but ensure it has the correct name
    let finalFile: File = compressedFile as File;
    
    // Always ensure it's a File instance (not a Blob)
    if (!(compressedFile instanceof File)) {
      const arrayBuffer = await (compressedFile as Blob).arrayBuffer();
      finalFile = new File(
        [arrayBuffer],
        file.name,
        {
          type: compressedFile.type || 'image/jpeg',
          lastModified: Date.now(),
        }
      );
    } else if (compressedFile.name !== file.name) {
      // If it's a File but name is different, create a new File with the original name
      const arrayBuffer = await compressedFile.arrayBuffer();
      finalFile = new File(
        [arrayBuffer],
        file.name, // Preserve original filename
        {
          type: compressedFile.type,
          lastModified: Date.now(),
        }
      );
    }

    // Final verification that we have a proper File instance
    if (!(finalFile instanceof File)) {
      throw new Error('Failed to create a valid File instance from compressed image');
    }

    return {
      compressed: finalFile,
      originalSize: file.size,
      compressedSize: finalFile.size,
      quality: finalQuality, // Return the quality that worked
    };
  } catch (error: any) {
    throw new Error(error.message || 'Image compression failed');
  }
};

/**
 * Compresses multiple image files
 * @param files - Array of image files to compress
 * @param maxFileSize - Maximum file size in bytes (default 5MB)
 * @param onProgress - Optional progress callback
 * @returns Array of compressed files with metadata
 */
export const compressImages = async (
  files: File[],
  maxFileSize: number = MAX_FILE_SIZE,
  onProgress?: (current: number, total: number) => void
): Promise<CompressionResult[]> => {
  const results: CompressionResult[] = [];
  
  for (let i = 0; i < files.length; i++) {
    try {
      const result = await compressImage(files[i], maxFileSize);
      results.push(result);
      if (onProgress) {
        onProgress(i + 1, files.length);
      }
    } catch (error: any) {
      // If compression fails, we could either skip the file or throw
      // For now, we'll throw to let the caller handle it
      throw new Error(`Failed to compress ${files[i].name}: ${error.message}`);
    }
  }
  
  return results;
};

